/**
 * Era-driven menu theming. Derives a small set of CSS custom properties from
 * an `EraDef.palette` (plain hex numbers, same values the renderer uses for
 * fog/sky/lights) and applies them to a root element (default: <html>), so
 * plain CSS (ui.css) can reference `var(--accent)`, `var(--bg-0)`, etc.
 *
 * Pure DOM/CSS — no three.js import here, so this stays usable from menu
 * screens that mount before a GameView exists.
 */
import type { ContentRegistry } from '../content/registry';
import type { EraDef } from '../content/types';
import type { SaveData } from '../meta/save';

interface RGB { r: number; g: number; b: number; }

function hexToRgb(hex: number): RGB {
  return { r: (hex >> 16) & 0xff, g: (hex >> 8) & 0xff, b: hex & 0xff };
}

function rgbToHex(c: RGB): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(c.r).toString(16).padStart(2, '0')}${clamp(c.g).toString(16).padStart(2, '0')}${clamp(c.b).toString(16).padStart(2, '0')}`;
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

/** Relative luminance per WCAG (sRGB, 0..1). */
function luminance(c: RGB): number {
  const chan = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(c.r) + 0.7152 * chan(c.g) + 0.0722 * chan(c.b);
}

/** Lighten a color toward white until it clears a minimum luminance, for contrast against dark backgrounds. */
function ensureLight(c: RGB, minLum = 0.35): RGB {
  let out = c;
  let t = 0;
  while (luminance(out) < minLum && t < 1) {
    t += 0.08;
    out = mix(c, { r: 255, g: 255, b: 255 }, t);
  }
  return out;
}

function rgba(c: RGB, a: number): string {
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${a})`;
}

/**
 * Sets --accent, --accent-soft, --bg-0, --bg-1, --text, --text-dim, --danger,
 * --warn on `root` (default document.documentElement), derived from
 * `era.palette`.
 */
export function applyEraTheme(era: EraDef, root: HTMLElement = document.documentElement): void {
  const p = era.palette;
  const accentRaw = hexToRgb(p.accent);
  const accent = ensureLight(accentRaw, 0.35);

  const skyRgb = hexToRgb(p.sky);
  const fogRgb = hexToRgb(p.fog);
  // Deep background: sky/fog mixed and darkened toward black.
  const bgBase = mix(skyRgb, fogRgb, 0.5);
  const bg0 = mix(bgBase, { r: 0, g: 0, b: 0 }, 0.82);
  const bg1 = mix(bgBase, { r: 0, g: 0, b: 0 }, 0.68);

  const dangerRaw = hexToRgb(p.enemyBullet);
  const danger = ensureLight(dangerRaw, 0.4);
  const warnRaw = hexToRgb(p.hazard);
  const warn = ensureLight(warnRaw, 0.45);

  const text = ensureLight(mix(accent, { r: 255, g: 255, b: 255 }, 0.6), 0.85);
  const textDim = mix(text, bg1, 0.45);

  const set = (name: string, value: string) => root.style.setProperty(name, value);
  set('--accent', rgbToHex(accent));
  set('--accent-soft', rgba(accent, 0.35));
  set('--accent-rgb', `${Math.round(accent.r)}, ${Math.round(accent.g)}, ${Math.round(accent.b)}`);
  set('--bg-0', rgbToHex(bg0));
  set('--bg-1', rgbToHex(bg1));
  set('--text', rgbToHex(text));
  set('--text-dim', rgbToHex(textDim));
  set('--danger', rgbToHex(danger));
  set('--warn', rgbToHex(warn));
}

/**
 * Picks the era whose palette should theme the menu: the highest era the
 * player is anchored to / can start from (mirrors the "Anchor: X" button
 * logic in mainMenu — `Math.max(0, ...save.anchors.map(x => x + 1))`),
 * falling back to era 0 if that index isn't available.
 */
export function eraThemeFor(save: SaveData, reg: ContentRegistry): EraDef {
  const maxEra = Math.max(0, ...save.anchors.map((x) => x + 1));
  return reg.eras[maxEra] ?? reg.eras[0];
}
